import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { CefrBadge } from '@/components/CefrBadge'
import { cn } from '@/components/ui/cn'
import { vocabularyApi } from '@/api/vocabulary'
import { copy } from '@/lib/copy'
import { isSenseLearned } from '@/lib/senseUtils'
import type { KnownSense, LocalizedSense } from '@/types/user'
import type { CefrLevel } from '@/types/common'

const MAX_VISIBLE_SENSES = 5

interface KnownMeaningsBlockProps {
  knownSenses: KnownSense[]
  localizedSenses: LocalizedSense[]
  primarySenseKey?: string
  userWordId: string
}

export function KnownMeaningsBlock({
  knownSenses,
  localizedSenses,
  primarySenseKey,
  userWordId,
}: KnownMeaningsBlockProps) {
  const queryClient = useQueryClient()
  const [otherExpanded, setOtherExpanded] = useState(false)
  const [showAllDictionary, setShowAllDictionary] = useState(false)

  const invalidateWord = () => {
    queryClient.invalidateQueries({ queryKey: ['vocabulary', userWordId] })
    queryClient.invalidateQueries({ queryKey: ['vocabulary'] })
  }

  const setPrimaryMutation = useMutation({
    mutationFn: (senseKey: string) => vocabularyApi.setPrimarySense(userWordId, senseKey),
    onSuccess: invalidateWord,
  })

  const removeSenseMutation = useMutation({
    mutationFn: (senseKey: string) => vocabularyApi.removeSense(userWordId, senseKey),
    onSuccess: invalidateWord,
  })

  if (knownSenses.length === 0 && localizedSenses.length === 0) {
    return null
  }

  const unknownSenses = localizedSenses.filter(
    (sense) => !isSenseLearned(sense.senseKey, sense.translation, knownSenses),
  )
  const collapseDictionary = unknownSenses.length > 3
  const showDictionary = !collapseDictionary || otherExpanded
  const visibleDictionary = showAllDictionary
    ? unknownSenses
    : unknownSenses.slice(0, MAX_VISIBLE_SENSES)
  const hiddenDictionaryCount = Math.max(unknownSenses.length - MAX_VISIBLE_SENSES, 0)
  const canRemoveSense = knownSenses.length > 1
  const actionsBusy = setPrimaryMutation.isPending || removeSenseMutation.isPending

  const glossForSense = (senseKey: string) =>
    localizedSenses.find((sense) => sense.senseKey === senseKey)?.englishGloss
      ?? localizedSenses.find((sense) => sense.senseKey === senseKey)?.nativeGloss

  const handleRemove = (sense: KnownSense) => {
    const message = copy.word.removeSenseConfirm.replace('{translation}', sense.translation)
    if (!window.confirm(message)) return
    removeSenseMutation.mutate(sense.senseKey)
  }

  return (
    <div className="mb-6 space-y-4" data-testid="known-meanings-block">
      {knownSenses.length > 0 && (
        <div className="rounded-xl border bg-card p-4">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Meanings
          </h2>
          <ul className="space-y-2">
            {knownSenses.map((sense) => {
              const isPrimary = sense.senseKey === primarySenseKey
              const gloss = glossForSense(sense.senseKey)
              return (
                <li
                  key={sense.senseKey}
                  className={cn(
                    'flex items-start justify-between gap-2 rounded-lg px-3 py-2',
                    isPrimary ? 'border border-primary/30 bg-primary/5' : 'bg-muted/20',
                  )}
                  data-testid="known-sense-row"
                >
                  <div className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                    <div>
                      <span className={cn('text-foreground', isPrimary && 'font-medium')}>
                        {sense.translation}
                      </span>
                      {gloss && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{gloss}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    {!isPrimary && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => setPrimaryMutation.mutate(sense.senseKey)}
                        disabled={actionsBusy}
                      >
                        {copy.word.setPrimarySense}
                      </button>
                    )}
                    {canRemoveSense && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-destructive hover:underline"
                        onClick={() => handleRemove(sense)}
                        disabled={actionsBusy}
                        data-testid="remove-sense-btn"
                      >
                        {copy.word.removeSense}
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {unknownSenses.length > 0 && (
        <div className="rounded-xl border bg-card p-4">
          {collapseDictionary ? (
            <button
              type="button"
              onClick={() => setOtherExpanded((v) => !v)}
              className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
              data-testid="dictionary-meanings-toggle"
            >
              <span>{copy.word.otherMeanings} ({unknownSenses.length})</span>
              {otherExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          ) : (
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {copy.word.otherMeanings} ({unknownSenses.length})
            </h3>
          )}
          {showDictionary && (
            <div
              className={cn('space-y-2', collapseDictionary ? 'mt-3' : 'mt-3')}
              data-testid="dictionary-meanings-list"
            >
              {visibleDictionary.map((sense) => (
                <div
                  key={sense.senseKey}
                  className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    {sense.partOfSpeech && (
                      <span className="text-xs italic text-muted-foreground">{sense.partOfSpeech}</span>
                    )}
                    {sense.cefrLevel && <CefrBadge level={sense.cefrLevel as CefrLevel} />}
                  </div>
                  <p className="mt-0.5 text-foreground">{sense.translation}</p>
                  {sense.englishGloss && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{sense.englishGloss}</p>
                  )}
                  {!sense.englishGloss && sense.nativeGloss && sense.nativeGloss !== sense.translation && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{sense.nativeGloss}</p>
                  )}
                </div>
              ))}
              {!showAllDictionary && hiddenDictionaryCount > 0 && (
                <button
                  type="button"
                  className="text-sm text-primary hover:underline"
                  onClick={() => setShowAllDictionary(true)}
                  data-testid="show-more-meanings"
                >
                  Show {hiddenDictionaryCount} more
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
